import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DEFAULT_BRANDING, buildBrandingCss, sanitizeBranding } from '../theme/brandingTokens';

export const DEFAULTS = DEFAULT_BRANDING;

const BrandingContext = createContext(null);
const STYLE_ID = 'nixia-branding-style';

/**
 * Version entreprise (NSIA) :
 * - Branding figé (pas de persistance localStorage, pas de rendu dynamique via API)
 * - Centralisation des couleurs/logo dans `src/theme/brandingTokens.js`
 */
export function BrandingProvider({ children }) {
  const [branding] = useState(sanitizeBranding({ ...DEFAULTS }));

  const brandingRef = useRef(branding);
  brandingRef.current = branding;

  const injectBrandingCss = () => {
    const theme = document.documentElement.getAttribute('data-theme') || 'dark';
    let el = document.getElementById(STYLE_ID);
    if (!el) {
      el = document.createElement('style');
      el.id = STYLE_ID;
    }
    document.head.appendChild(el);
    el.textContent = buildBrandingCss(brandingRef.current, theme);
  };

  // Re-inject the CSS whenever branding itself changes.
  useEffect(() => {
    injectBrandingCss();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branding]);

  // Observe data-theme flips once, for the component's lifetime, instead of
  // tearing down and re-attaching a MutationObserver on every branding change
  // (reads the latest branding via brandingRef so it never goes stale).
  useEffect(() => {
    const obs = new MutationObserver((muts) => {
      for (const m of muts) if (m.type === 'attributes' && m.attributeName === 'data-theme') injectBrandingCss();
    });
    obs.observe(document.documentElement, { attributes: true });
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!branding?.appName) return;
    try {
      document.title = branding.appName;
    } catch {}
  }, [branding?.appName]);

  const value = useMemo(
    () => ({
      branding,
      updateBranding: () => {},
      saveBranding: async () => branding,
      resetBranding: async () => branding,
      hydrated: true,
      scopeAdminId: null,
    }),
    [branding]
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding() {
  const ctx = useContext(BrandingContext);
  if (!ctx) throw new Error('useBranding must be used within BrandingProvider');
  return ctx;
}
